import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Cookie, Shield, Info } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after 1 second
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
  };

  const handleDecline = () => {
    // User declined - clear all non-essential data
    const essentialKeys = ['cookieConsent', 'cookieConsentDate'];
    Object.keys(localStorage).forEach(key => {
      if (!essentialKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    });
    
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    
    // Reload to clear state
    window.location.reload();
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/30 z-[9998]" />
      
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 animate-in slide-in-from-bottom duration-300" dir="rtl">
        <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200">
          <div className="p-6 md:p-8 text-right">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-orange-600" />
                </div>
                <div className="text-right">
                  <h3 className="text-xl font-bold text-gray-900">
                    نحن نحترم خصوصيتك 🍪
                  </h3>
                  <p className="text-sm text-gray-500">استخدام ملفات التخزين المحلي</p>
                </div>
              </div>
              <button
                onClick={handleDecline}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="رفض"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="mb-6 text-right">
              <p className="text-gray-700 text-base leading-relaxed mb-4 text-right">
                نستخدم التخزين المحلي (localStorage) لتحسين تجربتك في التطبيق، بما في ذلك حفظ تفضيلاتك، 
                تسجيل الدخول، والمطاعم المفضلة. لا نستخدم ملفات تتبع خارجية أو إعلانات.
              </p>

              {/* Details Toggle */}
              {!showDetails ? (
                <button
                  onClick={() => setShowDetails(true)}
                  className="text-orange-600 hover:text-orange-700 font-medium text-sm flex items-center gap-1 transition-colors"
                >
                  <Info className="w-4 h-4" />
                  عرض التفاصيل
                </button>
              ) : (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 text-right">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-orange-600 hover:text-orange-700 font-medium text-sm flex items-center gap-1 mb-3 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    إخفاء التفاصيل
                  </button>
                  
                  <div className="space-y-3 text-sm text-right">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-right">
                        <Shield className="w-4 h-4 text-green-600" />
                        ما نقوم بتخزينه:
                      </h4>
                      <ul className="list-disc list-inside text-gray-700 space-y-1 mr-4 text-right">
                        <li className="text-right"><strong>معلومات الحساب:</strong> بيانات تسجيل الدخول والملف الشخصي</li>
                        <li className="text-right"><strong>التفضيلات:</strong> المطاعم المفضلة وإعدادات التطبيق</li>
                        <li className="text-right"><strong>الذاكرة المؤقتة:</strong> لتسريع تحميل البيانات</li>
                        <li className="text-right"><strong>الموقع:</strong> لعرض المطاعم القريبة منك (إذا سمحت)</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 text-right">ما لا نفعله:</h4>
                      <ul className="list-disc list-inside text-gray-700 space-y-1 mr-4 text-right">
                        <li className="text-right">لا نبيع بياناتك لأي طرف ثالث</li>
                        <li className="text-right">لا نستخدم أدوات تتبع إعلانية</li>
                        <li className="text-right">لا نشارك معلوماتك مع أطراف خارجية</li>
                      </ul>
                    </div>

                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-gray-600 text-xs text-right">
                        يمكنك حذف جميع البيانات المحفوظة في أي وقت من إعدادات المتصفح أو بالضغط على "رفض".
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleAccept}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-md hover:shadow-lg"
              >
                قبول جميع الملفات
              </Button>
              <Button
                onClick={handleDecline}
                variant="outline"
                className="flex-1 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-all"
              >
                رفض الكل
              </Button>
            </div>

            {/* Footer Note */}
            <p className="text-xs text-gray-500 text-center mt-4 text-right">
              باستخدامك للتطبيق، فإنك توافق على{' '}
              <a href="#" className="text-orange-600 hover:underline">
                سياسة الخصوصية
              </a>{' '}
              و{' '}
              <a href="#" className="text-orange-600 hover:underline">
                شروط الاستخدام
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
